/* iter-114 — SOPDialog: SOPs button on the user screen.
 *
 * Layout (matches user spec):
 *   • Title "SOPs" with the "SOP maker" CTA in the top-right corner.
 *   • 13 departmental buttons in the exact order/case specified.
 *   • Clicking a department reveals the saved SOPs for that dept.
 *   • Clicking "SOP maker" opens the generator (department prefilled if
 *     a dept was already selected) → AI deep-research-style markdown.
 *   • Save persists into `sops` collection AND mirrors a link into the
 *     matching dept tile's "SOPs" folder.
 *
 * Role gating mirrors the backend: viewers can read; admin / engineer /
 * editor / department-head can generate + save.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import {
  ScrollText, Sparkles, ArrowLeft, Trash2, Loader2, Save, X, Upload,
} from 'lucide-react';
import FilePreviewThumb from './FilePreviewThumb';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Exact labels in the user-specified order — do NOT reorder or relabel.
const DEPARTMENTS = [
  'Food and Beverage', 'Rooms', 'Engineering', 'Spa',
  'Human Resources', 'Finance', 'Security', 'It',
  'Procurement', 'WATER SPORTS', 'Sales', 'Entertainment', 'Laundry',
];

const DEPT_COLORS = {
  'Food and Beverage': '#14B8A6',
  'Rooms':             '#84CC16',
  'Engineering':       '#F59E0B',
  'Spa':               '#BE185D',
  'Human Resources':   '#6366F1',
  'Finance':           '#15803D',
  'Security':          '#7F1D1D',
  'It':                '#475569',
  'Procurement':       '#0EA5E9',
  'WATER SPORTS':      '#0891B2',
  'Sales':             '#8B5CF6',
  'Entertainment':     '#DC2626',
  'Laundry':           '#7C3AED',
};

const EDITOR_ROLES = new Set(['admin', 'engineer', 'editor', 'department']);

function currentRole() {
  try { return (typeof window !== 'undefined' ? (localStorage.getItem('user_role') || '') : ''); } catch { return ''; }
}
function authHeaders() {
  const t = (typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null) || '';
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/* ──────────────────────────────────────────────────────────────────
 * Maker pane — generate + save
 * ────────────────────────────────────────────────────────────────── */
function SOPMakerPane({ department, onCancel, onSaved }) {
  const [topic, setTopic] = useState('');
  const [scopeHint, setScopeHint] = useState('');
  const [busy, setBusy] = useState(false);
  const [markdown, setMarkdown] = useState('');
  const [modelUsed, setModelUsed] = useState('');
  const [title, setTitle] = useState('');
  const color = DEPT_COLORS[department] || '#0F766E';

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/sops/generate`,
        { department, topic, scope_hint: scopeHint, prefer: 'openai' },
        { headers: authHeaders(), timeout: 120000 }
      );
      setMarkdown(data.markdown || '');
      setModelUsed(data.model_used || '');
      // Try to lift the H1 from the markdown as default title
      const firstH1 = (data.markdown || '').match(/^#\s+(.+)$/m);
      setTitle((firstH1 && firstH1[1]) || (topic ? `${department} · ${topic}` : `${department} SOP`));
      toast.success('SOP draft generated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed');
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!markdown || markdown.length < 50) {
      toast.error('Generate or paste the SOP body first');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/sops/save`,
        {
          department, title, markdown, topic, model_used: modelUsed,
          mirror_to_tile: true,
        },
        { headers: authHeaders() }
      );
      toast.success(`Saved · mirrored ${data.mirrored_link_id ? '✔' : '✘ (no matching tile)'}`);
      onSaved?.(data.saved);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="sop-maker-pane">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4" style={{ color }} />
        <h3 className="text-sm font-bold uppercase tracking-[0.18em]" style={{ fontFamily: 'Manrope', color }}>
          SOP maker · {department}
        </h3>
      </div>

      {/* Inputs */}
      <div className="space-y-2">
        <input
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="Topic (e.g. 'Cleaning a guest suite after check-out')"
          className="w-full text-[13px] rounded-md border px-3 py-2"
          style={{ borderColor: '#D1D5DB', fontFamily: 'Manrope' }}
          data-testid="sop-maker-topic-input"
        />
        <textarea
          value={scopeHint}
          onChange={(e) => setScopeHint(e.target.value)}
          placeholder="Optional scope notes (regulations to include, location specifics, etc.)"
          rows={2}
          className="w-full text-[12px] rounded-md border px-3 py-2"
          style={{ borderColor: '#D1D5DB', fontFamily: 'Manrope' }}
          data-testid="sop-maker-scope-input"
        />
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={generate}
          disabled={busy}
          className="inline-flex items-center gap-1.5 text-[12px] font-bold px-3 py-2 rounded-md text-white disabled:opacity-50"
          style={{ background: color, fontFamily: 'Manrope' }}
          data-testid="sop-maker-generate-btn"
        >
          {busy && !markdown ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {busy && !markdown ? 'Researching…' : (markdown ? 'Regenerate' : 'Generate with AI')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-[12px] font-bold px-3 py-2 rounded-md"
          style={{ background: '#EAE5D9', color: '#4A5D5D', fontFamily: 'Manrope' }}
        >
          Cancel
        </button>
      </div>

      {/* Editor + preview */}
      {markdown && (
        <>
          <div className="pt-3 border-t" style={{ borderColor: '#EAE5D9' }}>
            <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#6B7280', fontFamily: 'Manrope' }}>
              Title
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full mt-1 text-[13px] rounded-md border px-3 py-2"
              style={{ borderColor: '#D1D5DB', fontFamily: 'Manrope' }}
              data-testid="sop-maker-title-input"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-3 mt-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#6B7280', fontFamily: 'Manrope' }}>
                Markdown (editable)
              </label>
              <textarea
                value={markdown}
                onChange={(e) => setMarkdown(e.target.value)}
                rows={18}
                className="w-full mt-1 text-[11px] rounded-md border px-2 py-2 font-mono"
                style={{ borderColor: '#D1D5DB', fontFamily: 'ui-monospace,Menlo,Consolas,monospace' }}
                data-testid="sop-maker-markdown-input"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#6B7280', fontFamily: 'Manrope' }}>
                Preview
              </label>
              <div
                className="mt-1 max-h-[420px] overflow-y-auto rounded-md border px-3 py-2 text-[12px] prose prose-sm max-w-none"
                style={{ borderColor: '#D1D5DB', background: '#FAFAF7' }}
                data-testid="sop-maker-preview"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
              </div>
            </div>
          </div>

          <p className="text-[10px] italic" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
            Generated with {modelUsed || 'AI'} · {markdown.length} chars · also creates an “SOPs” folder link inside the matching department tile.
          </p>

          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-1.5 text-[12px] font-bold px-3 py-2.5 rounded-md text-white disabled:opacity-50"
            style={{ background: '#1A2E2E', fontFamily: 'Manrope' }}
            data-testid="sop-maker-save-btn"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {busy ? 'Saving…' : 'Save SOP'}
          </button>
        </>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * SOP detail (read mode) — markdown viewer with delete (editor+)
 * ────────────────────────────────────────────────────────────────── */
function SOPDetailPane({ sopId, onBack, onDeleted, canEdit }) {
  const [sop, setSop] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/sops/${sopId}`)
      .then(({ data }) => { if (!cancelled) setSop(data); })
      .catch(() => { if (!cancelled) toast.error('SOP not found'); });
    return () => { cancelled = true; };
  }, [sopId]);
  if (!sop) {
    return (
      <div className="flex items-center justify-center py-8 text-sm" style={{ color: '#6B7280' }}>
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
      </div>
    );
  }
  const remove = async () => {
    if (!window.confirm(`Delete "${sop.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/sops/${sopId}`, { headers: authHeaders() });
      toast.success('SOP deleted');
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };
  return (
    <div data-testid="sop-detail-pane">
      <div className="flex items-center gap-2 mb-2">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-[12px] font-bold text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <span className="text-[10px] italic" style={{ color: '#7A8C8C' }}>
          {sop.department} · saved {new Date(sop.created_at).toLocaleString()}
        </span>
        {canEdit && (
          <button
            type="button"
            onClick={remove}
            className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold text-red-600 hover:bg-red-50 rounded px-2 py-1"
            data-testid="sop-detail-delete-btn"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        )}
      </div>
      <div
        className="prose prose-sm max-w-none rounded-md border px-3 py-2 max-h-[60vh] overflow-y-auto text-[12.5px]"
        style={{ borderColor: '#EAE5D9', background: '#FAFAF7' }}
      >
        {sop.kind === 'uploaded' && sop.file_url ? (
          <div className="flex gap-4 items-start">
            <FilePreviewThumb url={sop.file_url} filename={sop.filename} size="xl" color="#0F766E" rounded="rounded-lg" />
            <div className="min-w-0 flex-1">
              <p className="text-[12px] mb-2"><strong>Uploaded file:</strong> {sop.filename}</p>
              <a
                href={sop.file_url.startsWith('http') ? sop.file_url : `${process.env.REACT_APP_BACKEND_URL}${sop.file_url}`}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-bold text-white"
                style={{ background: '#0F766E', fontFamily: 'Manrope' }}
                data-testid="sop-detail-open-file-btn"
              >
                Open file →
              </a>
            </div>
          </div>
        ) : (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{sop.markdown}</ReactMarkdown>
        )}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Main dialog
 * ────────────────────────────────────────────────────────────────── */
export default function SOPDialog({ open, onClose }) {
  const canEdit = EDITOR_ROLES.has(currentRole());
  const [counts, setCounts] = useState({});
  const [selectedDept, setSelectedDept] = useState(null);
  const [sops, setSops] = useState([]);
  const [view, setView] = useState('home'); // 'home' | 'dept' | 'maker' | 'detail'
  const [activeSopId, setActiveSopId] = useState(null);
  const [loading, setLoading] = useState(false);

  const reloadCounts = () => {
    axios.get(`${API}/sops/departments`)
      .then(({ data }) => {
        const map = {};
        (data.departments || []).forEach((d) => { map[d.name] = d.count; });
        setCounts(map);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!open) return;
    reloadCounts();
    // Deep-link support: if URL is /app?sop=<id>, auto-open that SOP. Otherwise reset to home.
    let deepLinkApplied = false;
    try {
      const sopId = new URLSearchParams(window.location.search).get('sop');
      if (sopId) {
        deepLinkApplied = true;
        axios
          .get(`${API}/sops/${sopId}`)
          .then(({ data }) => {
            setSelectedDept(data.department);
            setActiveSopId(sopId);
            setView('detail');
          })
          .catch(() => { setView('home'); setSelectedDept(null); });
      }
    } catch { /* swallow */ }
    if (!deepLinkApplied) {
      setView('home');
      setSelectedDept(null);
    }
  }, [open]);

  const openDept = (dept) => {
    setSelectedDept(dept);
    setView('dept');
    setLoading(true);
    axios.get(`${API}/sops?department=${encodeURIComponent(dept)}`)
      .then(({ data }) => setSops(data.sops || []))
      .catch(() => setSops([]))
      .finally(() => setLoading(false));
  };

  const openMaker = () => {
    if (!canEdit) {
      toast.error('Sign in as an editor or above to generate SOPs');
      return;
    }
    setView('maker');
  };

  // iter-116 — File-upload button (PDF / Word). Lands a row in `sops`
  // with kind='uploaded' + mirrors a link into the matching dept tile.
  const uploadInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const triggerUpload = () => {
    if (!canEdit) {
      toast.error('Sign in as an editor or above to upload SOPs');
      return;
    }
    if (!selectedDept) {
      toast.error('Open a department first, then click Upload');
      return;
    }
    uploadInputRef.current?.click();
  };
  const handleUploadFile = async (file) => {
    if (!file || !selectedDept) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('department', selectedDept);
      fd.append('title', file.name.replace(/\.[^.]+$/, ''));
      const { data } = await axios.post(`${API}/admin/sops/upload`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token') || ''}`, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded · mirrored ${data.mirrored_link_id ? '✔' : '✘'}`);
      reloadCounts();
      openDept(selectedDept);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (uploadInputRef.current) uploadInputRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="sop-dialog" className="w-[95vw] max-w-3xl p-0">
        <div className="px-5 sm:px-6 pt-5 pb-4 border-b flex items-start gap-3" style={{ borderColor: '#EAE5D9', background: 'linear-gradient(135deg,#0F766E,#0F766Ecc)' }}>
          <DialogHeader className="flex-1">
            <DialogTitle className="text-2xl text-white flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              <ScrollText className="w-5 h-5" /> SOP&apos;s
            </DialogTitle>
            <DialogDescription className="text-[11px] uppercase tracking-wider font-bold mt-1" style={{ color: 'rgba(255,255,255,.85)', fontFamily: 'Manrope' }}>
              {selectedDept ? `${selectedDept} · ${(sops || []).length} saved` : `${DEPARTMENTS.length} departments`}
            </DialogDescription>
          </DialogHeader>
          {/* Top-right Upload + SOP maker CTAs */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={triggerUpload}
              disabled={!canEdit || uploading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-bold disabled:opacity-40"
              style={{ background: 'white', color: '#0F766E', fontFamily: 'Manrope' }}
              data-testid="sop-upload-btn"
              title={canEdit ? (selectedDept ? `Upload PDF/Word to ${selectedDept}` : 'Open a department first, then click Upload') : 'Editor+ only'}
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              Upload
            </button>
            <input
              ref={uploadInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={(e) => handleUploadFile(e.target.files?.[0])}
              className="hidden"
              data-testid="sop-upload-input"
            />
            <button
              type="button"
              onClick={openMaker}
              disabled={!canEdit}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-bold disabled:opacity-40"
              style={{ background: 'white', color: '#0F766E', fontFamily: 'Manrope' }}
              data-testid="sop-maker-btn"
              title={canEdit ? 'Open SOP maker' : 'Sign in as editor or above to generate SOPs'}
            >
              <Sparkles className="w-3.5 h-3.5" /> SOP maker
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-5 max-h-[70vh] overflow-y-auto">
          {/* HOME — 13 department tiles */}
          {view === 'home' && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {DEPARTMENTS.map((dept) => {
                const color = DEPT_COLORS[dept] || '#0F766E';
                const n = counts[dept] || 0;
                return (
                  <button
                    type="button"
                    key={dept}
                    onClick={() => openDept(dept)}
                    className="text-left p-3 rounded-lg border transition-all hover:-translate-y-0.5 hover:shadow-md"
                    style={{ borderColor: `${color}33`, background: 'white', borderLeftWidth: 4, borderLeftColor: color }}
                    data-testid={`sop-dept-${dept.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <div className="text-[12px] font-bold" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
                      {dept}
                    </div>
                    <div className="text-[10px]" style={{ color, fontFamily: 'Manrope' }}>
                      {n} SOP{n === 1 ? '' : 's'}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* DEPT — list saved SOPs for that department */}
          {view === 'dept' && selectedDept && (
            <div data-testid="sop-dept-view">
              <button
                type="button"
                onClick={() => { setView('home'); setSelectedDept(null); }}
                className="inline-flex items-center gap-1 text-[12px] font-bold text-gray-600 hover:text-gray-900 mb-3"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> All departments
              </button>
              {loading && (
                <div className="text-sm py-6 text-center" style={{ color: '#6B7280' }}>
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                </div>
              )}
              {!loading && (sops || []).length === 0 && (
                <div className="text-center py-8" style={{ color: '#6B7280', fontFamily: 'Manrope' }}>
                  <p className="text-sm italic">No SOPs saved yet for <strong>{selectedDept}</strong>.</p>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={openMaker}
                      className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-bold text-white"
                      style={{ background: DEPT_COLORS[selectedDept] || '#0F766E', fontFamily: 'Manrope' }}
                      data-testid="sop-dept-make-first-btn"
                    >
                      <Sparkles className="w-3.5 h-3.5" /> Make the first SOP
                    </button>
                  )}
                </div>
              )}
              {!loading && (sops || []).length > 0 && (
                <ul className="space-y-2">
                  {sops.map((s) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => { setActiveSopId(s.id); setView('detail'); }}
                        className="w-full text-left p-3 rounded-lg border hover:shadow-sm transition-shadow flex items-center gap-3"
                        style={{ borderColor: '#EAE5D9', background: 'white' }}
                        data-testid={`sop-row-${s.id}`}
                      >
                        {s.kind === 'uploaded' && s.file_url && (
                          <FilePreviewThumb url={s.file_url} filename={s.filename} size="md" color={DEPT_COLORS[selectedDept] || '#0F766E'} />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-bold truncate" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>{s.title}</div>
                          <div className="text-[10px] mt-0.5" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                            {new Date(s.created_at).toLocaleString()} · {s.model_used || 'AI'} · {s.created_by || 'unknown'}
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* MAKER — generate + save */}
          {view === 'maker' && (
            <SOPMakerPane
              department={selectedDept || DEPARTMENTS[0]}
              onCancel={() => setView(selectedDept ? 'dept' : 'home')}
              onSaved={() => { reloadCounts(); if (selectedDept) openDept(selectedDept); else setView('home'); }}
            />
          )}

          {/* DETAIL — view one SOP */}
          {view === 'detail' && activeSopId && (
            <SOPDetailPane
              sopId={activeSopId}
              canEdit={canEdit}
              onBack={() => { setView('dept'); setActiveSopId(null); }}
              onDeleted={() => { reloadCounts(); openDept(selectedDept); }}
            />
          )}
        </div>

        <div className="px-5 sm:px-6 py-3 border-t flex justify-end" style={{ borderColor: '#EAE5D9', background: 'white' }}>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1 text-xs font-bold px-3 py-2 rounded-lg"
            style={{ background: '#EAE5D9', color: '#4A5D5D', fontFamily: 'Manrope' }}
          >
            <X className="w-3.5 h-3.5" /> Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
